import React from 'react';
import { fmtDate, fmtDateTime } from '../../utils/formatters';
import ClientPopover from '../ClientPopover';
import { BoletoItem, boletoCapabilities } from './boletoCapabilities';
import { TONE_BADGE, TONE_TEXT } from '../../utils/statusColors';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaCliente, CelulaDinheiro, CelulaPrazo,
  barraDoEstado,
} from '../shared/lista/PecasDaLista';
import { tomDoAtraso } from '../../utils/recebiveis';
import MaisAcoes from '../shared/MaisAcoes';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const statusConfig: Record<string, { label: string; color: string }> = {
  registrado: { label: 'Registrado', color: TONE_BADGE.progress },
  // Cyan proposital: distingue "Confirmado pelo banco" de "Registrado" (ambos seriam progress)
  confirmado: { label: 'Confirmado', color: 'bg-cyan-500/20 text-cyan-400' },
  baixa_solicitada: { label: 'Baixa Solicitada', color: 'bg-orange-500/20 text-orange-400' },
  // E1 (22/10/2026, como o "Paga" de Recebíveis): pago é o desfecho normal, neutro; a cor fica para o que pede ação.
  pago: { label: 'Pago', color: TONE_BADGE.neutral },
  rejeitado: { label: 'Rejeitado', color: TONE_BADGE.danger },
  baixado: { label: 'Baixado', color: TONE_BADGE.neutral },
  // Emissão via API travada: o título PODE existir no Sicredi. Exige
  // conferência no portal antes de reemitir (senão duplica a cobrança).
  emissao_em_andamento: { label: '⚠ Emissão travada', color: TONE_BADGE.warning },
};

// Eventos da trilha de boletos da fatura (invoices.boletoHistory)
export const histEventConfig: Record<string, { label: string; color: string }> = {
  registrado: { label: 'Registrado', color: 'text-blue-400' },
  baixa_solicitada: { label: 'Baixa solicitada', color: 'text-orange-400' },
  confirmado: { label: 'Confirmado pelo banco', color: 'text-cyan-400' },
  rejeitado: { label: 'Rejeitado', color: 'text-red-400' },
  pago: { label: 'Pago', color: TONE_TEXT.success },
  baixado: { label: 'Baixado (cancelado)', color: 'text-gray-400' },
  movimento_boleto_antigo: { label: 'Boleto substituído', color: 'text-slate-400' },
  liquidado_webhook: { label: 'Liquidado (webhook Sicredi)', color: TONE_TEXT.success },
  liquidado_conciliacao: { label: 'Liquidado (conciliação Sicredi)', color: TONE_TEXT.success },
  emissao_iniciada: { label: 'Emissão iniciada', color: 'text-slate-400' },
  falha_emissao: { label: 'Falha na emissão', color: 'text-red-400' },
  emissao_indeterminada: { label: '⚠ Emissão indeterminada', color: 'text-amber-400' },
};

// A ordenação de sempre (o estado vem da tela), ligada às colunas do cabeçalho da lista.
const coluna = (c: string, sort: any, toggleSort: (col: string) => void) => ({
  ordem: sort?.col === c ? sort.dir : (false as const),
  onOrdenar: () => toggleSort(c),
});

const IconBtn: React.FC<{ title: string; onClick: () => void; className?: string; children: React.ReactNode }> = ({
  title, onClick, className = '', children,
}) => (
  <button onClick={onClick} title={title} className={`p-1.5 rounded hover:bg-slate-600 transition-colors ${className}`}>
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{children}</svg>
  </button>
);

interface Props {
  boletos: BoletoItem[];
  loading: boolean;
  loadError: string | null;
  searchFilter: string;
  sort: any;
  toggleSort: (col: string) => void;
  selected: Set<number>;
  selectableCount: number;
  onToggleSelect: (id: number) => void;
  onToggleAll: () => void;
  expandedHistId: number | null;
  onToggleHist: (id: number) => void;
  onReload: () => void;
  onEditDueDate: (b: BoletoItem) => void;
  onEmitirHibrido: (b: BoletoItem) => void;
  onBaixarApi: (b: BoletoItem) => void;
  onDownloadPdf: (b: BoletoItem) => void;
}

const BoletosTable: React.FC<Props> = ({
  boletos, loading, loadError, searchFilter, sort, toggleSort, selected, selectableCount,
  onToggleSelect, onToggleAll, expandedHistId, onToggleHist, onReload,
  onEditDueDate, onEmitirHibrido, onBaixarApi, onDownloadPdf,
}) => (
  // Padrão das listas no Financeiro, tela 3 (22/10/2026): a casca, a tabela e o cabeçalho das peças. SEM overflow de
  // rolagem (o cabeçalho fixa no <main>); o ⋯ das ações é fixo e não precisa da caixa.
  <CascaDaLista>
      <TabelaDaLista>
        <CabecalhoDaLista>
            <ColunaDaLista
              largura="w-12"
              rotulo={selectableCount > 0 ? (
                <input
                  type="checkbox"
                  aria-label="Selecionar todos"
                  checked={selectableCount > 0 && boletos.filter(b => boletoCapabilities(b).isSelectable).every(b => selected.has(b.id))}
                  onChange={onToggleAll}
                  className="rounded bg-slate-700 border-slate-600"
                />
              ) : null}
            />
            <ColunaDaLista rotulo="Fatura" largura="w-48" {...coluna('code', sort, toggleSort)} />
            <ColunaDaLista rotulo="Cliente" {...coluna('client', sort, toggleSort)} />
            <ColunaDaLista rotulo="Valor" largura="w-32" alinhamento="direita" {...coluna('totalValue', sort, toggleSort)} />
            <ColunaDaLista rotulo="Vencimento" largura="w-40" {...coluna('dueDate', sort, toggleSort)} />
            <ColunaDaLista rotulo="Status" largura="w-40" {...coluna('boletoStatus', sort, toggleSort)} />
            <ColunaDaLista rotulo="Pagamento" largura="w-44" {...coluna('payment', sort, toggleSort)} />
            <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-32" />
        </CabecalhoDaLista>
        <tbody className="divide-y divide-slate-800">
          {loading ? (
            <tr><td colSpan={8} className="p-8 text-center text-slate-400">Carregando...</td></tr>
          ) : boletos.length === 0 ? (
            <tr>
              <td colSpan={8} className="py-16">
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="p-4 bg-slate-700/50 rounded-full mb-4">
                    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>
                  </div>
                  {loadError ? (
                    <>
                      <p className="text-red-400 font-medium">Erro ao carregar os boletos</p>
                      <p className="text-slate-500 text-sm mt-1">{loadError}</p>
                      <button onClick={onReload} className="mt-3 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors">
                        Tentar novamente
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-slate-400 font-medium">{searchFilter ? 'Nenhum resultado encontrado' : 'Nenhuma fatura encontrada'}</p>
                      <p className="text-slate-500 text-sm mt-1">{searchFilter ? 'Tente buscar com outros termos' : 'Verifique faturas abertas com metodo boleto'}</p>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ) : (
            boletos.map(b => {
              const cap = boletoCapabilities(b);
              const statusCfg = b.boletoStatus ? statusConfig[b.boletoStatus] : null;
              // Boleto ANTERIOR (nossoNumero ≠ atual) mais recente na trilha
              const prevBoleto = (() => {
                const hist = b.boletoHistory || [];
                const current = (b.nossoNumero || '').trim();
                for (let i = hist.length - 1; i >= 0; i--) {
                  const nn = (hist[i].nossoNumero || '').trim();
                  if (nn && nn !== current) {
                    return { nossoNumero: nn, label: histEventConfig[hist[i].event]?.label || hist[i].event };
                  }
                }
                return null;
              })();
              const tomDaLinha = cap.needsAttention ? 'warning' as const : cap.isOverdue ? tomDoAtraso(cap.daysOverdue) : null;
              const displayName = b.clientDisplayName || b.clientTradeName || b.clientName;
              const showSubName = b.clientName && b.clientName !== displayName;
              return (
                <React.Fragment key={b.id}>
                  {/* K2 (12/10/2026): a linha não pinta mais (era vermelha na vencida e âmbar na de atenção). O estado vai na
                      barra lateral da primeira célula, no ícone e no peso, como em Recebíveis (#467). */}
                  <LinhaDaLista>
                    <td
                      className={`${CELULA} ${barraDoEstado(tomDaLinha)}`}
                      data-tom-da-linha={tomDaLinha || undefined}
                    >
                      {cap.isSelectable && (
                        <input
                          type="checkbox"
                          checked={selected.has(b.id)}
                          onChange={() => onToggleSelect(b.id)}
                          className="rounded bg-slate-700 border-slate-600"
                        />
                      )}
                    </td>
                    <td className={CELULA}>
                      <span className="text-white font-mono text-sm">{b.code}</span>
                      {cap.needsAttention && (
                        <span className="ml-1.5 text-amber-400" title={cap.attentionLabel || ''}>⚠</span>
                      )}
                      {b.nossoNumero && (
                        <div className="text-xs text-slate-500 font-mono truncate" title={`Nosso número: ${b.nossoNumero}`}>
                          {b.nossoNumero}
                          {/* E1 com UMA exceção (Heitor, 22/10/2026, ao aprovar a #574): o ⚡PIX mantém o verde, no tom success do statusColors
                              (nunca cor escrita à mão). É o que distingue o título da API, que o cliente consegue pagar, do de remessa. O
                              Pago segue neutro. */}
                          {b.hibrido && <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold font-sans ${TONE_BADGE.success}`} data-selo="pix" title="Boleto híbrido registrado via API do Sicredi: tem QR PIX e a 2ª via impressa é a OFICIAL do banco">⚡PIX</span>}
                        </div>
                      )}
                      {prevBoleto && (
                        <div className="text-[10px] text-slate-500 truncate">
                          anterior: <span className="font-mono">{prevBoleto.nossoNumero}</span> ({prevBoleto.label.toLowerCase()})
                        </div>
                      )}
                    </td>
                    {/* C1: o nome (que abre o popover do cliente) e UMA sub-linha, razão · CNPJ (o CNPJ era uma 3ª linha). */}
                    <CelulaCliente
                      nome={<ClientPopover clientId={b.clientId} block>{displayName}</ClientPopover>}
                      razao={showSubName ? b.clientName : null}
                      cnpj={b.clientCnpj}
                    />
                    <CelulaDinheiro valor={b.totalValue} />
                    {/* O vencimento é a peça CelulaPrazo (a Situação de Recebíveis): a data e, vencido, o ícone no tom do atraso
                        e o prazo em negrito; a vencer, o prazo neutro. Pago ou cancelado não tem prazo (o Status já diz). */}
                    <CelulaPrazo
                      vencimento={b.dueDate}
                      mostrarData
                      quitado={!cap.isOverdue && (b.status === 'Paga' || !!b.boletoPaymentDate || cap.dueStatus === 'overdue')}
                      rotuloQuitado={null}
                    />
                    <td className={`${CELULA} whitespace-nowrap`}>
                      {statusCfg ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                      ) : (
                        <span className="text-slate-500 text-xs">{b.nossoNumero ? '-' : 'Sem boleto'}</span>
                      )}
                    </td>
                    <td className={`${CELULA} whitespace-nowrap`}>
                      {b.boletoPaymentDate ? (
                        <div>
                          <span className="text-slate-300 text-xs">{fmtDate(b.boletoPaymentDate)}</span>
                          {b.boletoPaidValue && <span className="text-slate-400 text-xs ml-2">R$ {fmt(b.boletoPaidValue)}</span>}
                          {b.jurosMulta && Number(b.jurosMulta) > 0 && (
                            <span className="text-amber-400 text-xs ml-1">(+R$ {fmt(b.jurosMulta)})</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className={CELULA}>
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn title="Alterar Vencimento" onClick={() => onEditDueDate(b)} className="text-slate-400 hover:text-white">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </IconBtn>
                        {cap.canDownloadPdf && (
                          <IconBtn
                            title={b.hibrido
                              ? 'Baixar boleto OFICIAL do Sicredi (mesmo arquivo que o cliente recebe)'
                              : 'Baixar boleto (layout interno, título registrado por CNAB)'}
                            onClick={() => onDownloadPdf(b)}
                            className="text-slate-300 hover:text-white"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </IconBtn>
                        )}
                        {/* K5 (12/10/2026, no molde do D2): na linha ficam Vencimento e PDF; Emitir, Histórico e Baixar (sem
                            volta, separado por traço) moram no ⋯. Fixo: a tabela rola na horizontal e cortaria o menu. */}
                        <MaisAcoes
                          ancoragem="fixa"
                          title={`Mais ações do boleto ${b.code}`}
                          itens={[
                            ...(cap.canEmitirHibrido ? [{ label: 'Emitir boleto (PIX/híbrido)', onClick: () => onEmitirHibrido(b), title: 'Registra via API do Sicredi: QR PIX + linha digitável, sem remessa' }] : []),
                            ...((b.boletoHistory?.length || 0) > 0 ? [{ label: expandedHistId === b.id ? 'Ocultar histórico' : 'Histórico do boleto', onClick: () => onToggleHist(b.id) }] : []),
                            ...(cap.canBaixarApi ? [{ label: 'Baixar (cancelar) no Sicredi', onClick: () => onBaixarApi(b), danger: true, separadoAntes: true, title: 'Imediato e definitivo no banco' }] : []),
                          ]}
                        />
                      </div>
                    </td>
                  </LinhaDaLista>
                  {cap.needsAttention && (
                    <tr>
                      <td colSpan={8} className={`px-6 pb-2 pt-0 text-[11px] ${TONE_TEXT.warning}`}>⚠ {cap.attentionLabel}</td>
                    </tr>
                  )}
                  {expandedHistId === b.id && (b.boletoHistory?.length || 0) > 0 && (
                    <tr className="border-b border-slate-700/50 bg-slate-800/60">
                      <td colSpan={8} className="px-6 py-3">
                        <div className="text-xs text-slate-400 font-medium mb-2">Histórico de boletos — {b.code}</div>
                        <div className="space-y-1">
                          {[...(b.boletoHistory || [])].reverse().map((h: any, i: number) => {
                            const cfg = histEventConfig[h.event] || { label: h.event, color: 'text-slate-300' };
                            return (
                              <div key={i} className="flex items-center gap-3 text-xs">
                                <span className="text-slate-500 w-32 shrink-0">{fmtDateTime(h.at)}</span>
                                <span className={`font-medium w-40 shrink-0 ${cfg.color}`}>{cfg.label}</span>
                                {h.nossoNumero && <span className="text-slate-400 font-mono w-24 shrink-0">{h.nossoNumero}</span>}
                                {h.dueDate && <span className="text-slate-500 shrink-0">venc. {fmtDate(h.dueDate)}</span>}
                                {h.detail && <span className="text-slate-500 truncate">{h.detail}</span>}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </TabelaDaLista>
  </CascaDaLista>
);

export default BoletosTable;
